import React from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAppUsers } from '@/hooks/useAppUsers';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, UserCog, Users, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

interface AssignmentLog {
  id: string;
  deal_id: string;
  from_user_id: string | null;
  to_user_id: string | null;
  target_field: string | null;
  reason: string | null;
  changed_by: string | null;
  created_at: string;
}

interface AssignmentHistoryTabProps {
  dealId: string;
}

export function AssignmentHistoryTab({ dealId }: AssignmentHistoryTabProps) {
  const { users } = useAppUsers();

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['deal-assignment-log', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deal_assignment_log')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as AssignmentLog[];
    },
  });

  const getUserName = (userId: string | null) => {
    if (!userId) return 'Não atribuído';
    const user = users.find(u => u.id === userId);
    if (!user) return userId.slice(0, 8);
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Sem nome';
  };

  const getReasonLabel = (reason: string | null) => {
    switch (reason) {
      case 'auto_round_robin': return 'Round Robin';
      case 'manual': return 'Manual';
      case 'bulk_reassign': return 'Reassign em Massa';
      default: return reason || 'Manual';
    }
  };

  const getReasonVariant = (reason: string | null): 'default' | 'secondary' | 'outline' => {
    switch (reason) {
      case 'auto_round_robin': return 'default';
      case 'bulk_reassign': return 'secondary';
      default: return 'outline';
    }
  };

  const getFieldLabel = (field: string | null) => {
    switch (field) {
      case 'sales_consultant': return 'Consultor';
      case 'sales_development': return 'SDR';
      default: return field || 'Responsável';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Carregando histórico...
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <UserCog className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Nenhum registro de atribuição encontrado.</p>
          <p className="text-xs mt-1">O histórico será registrado a partir de agora.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <UserCog className="w-5 h-5" />
          Histórico de Atribuição
        </h3>
        <div className="space-y-3">
          {logs.map((log) => (
            <div
              key={log.id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card"
            >
              <div className="flex-shrink-0">
                {log.reason === 'bulk_reassign' ? (
                  <Users className="w-4 h-4 text-muted-foreground" />
                ) : log.reason === 'auto_round_robin' ? (
                  <RefreshCw className="w-4 h-4 text-primary" />
                ) : (
                  <UserCog className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={getReasonVariant(log.reason)}>
                    {getReasonLabel(log.reason)}
                  </Badge>
                  <Badge variant="outline">{getFieldLabel(log.target_field)}</Badge>
                </div>
                <div className="flex items-center gap-2 mt-1 text-sm">
                  <span className="text-muted-foreground">
                    {getUserName(log.from_user_id)}
                  </span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium">
                    {getUserName(log.to_user_id)}
                  </span>
                </div>
                {log.changed_by && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Por: {getUserName(log.changed_by)}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground text-right flex-shrink-0">
                {safeFormat(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
